import React, { useMemo } from "react";
import { RefreshCw } from "lucide-react";
import { Box, Button, Flex, Image, Text, VStack, useColorModeValue } from "@chakra-ui/react";

interface NoRecommendationsPageProps {
  onRefresh?: () => void;
}

const KEYFRAMES = `
@keyframes no-reco-ping {
  0% { transform: scale(0.88); opacity: 0.5; }
  100% { transform: scale(1.42); opacity: 0; }
}
`;

type LocalUser = {
  profileImageUri?: string;
  profileImageUrl?: string;
  firstName?: string;
  lastName?: string;
  initials?: string;
};

function resolveAvatarSrc(user: LocalUser | null): string | null {
  const uri = user?.profileImageUri || user?.profileImageUrl;
  if (!uri) return null;
  if (/^https?:\/\//i.test(uri)) return uri;
  return `https://payinapoapistorage.blob.core.windows.net/images/${uri}`;
}

function getInitials(user: LocalUser | null): string {
  if (!user) return "?";
  if (user.initials) return user.initials.slice(0, 2).toUpperCase();
  const a = (user.firstName?.[0] || "").toUpperCase();
  const b = (user.lastName?.[0] || "").toUpperCase();
  const pair = `${a}${b}`;
  return pair || "?";
}

export function NoRecommendationsPage({ onRefresh }: NoRecommendationsPageProps) {
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? (JSON.parse(raw) as LocalUser) : null;
    } catch {
      return null;
    }
  }, []);

  const avatarSrc = resolveAvatarSrc(user);
  const initials = getInitials(user);

  const shellBg = useColorModeValue(
    "linear-gradient(165deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.98) 100%)",
    "linear-gradient(165deg, rgba(22,22,24,0.92) 0%, rgba(10,10,11,0.98) 100%)"
  );
  const shellBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const shellShadow = useColorModeValue(
    "0 12px 40px rgba(15,23,42,0.08)",
    "0 20px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)"
  );
  const ringColor = useColorModeValue("rgba(34,197,94,0.38)", "rgba(52,211,153,0.35)");
  const titleColor = useColorModeValue("gray.900", "white");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const avatarBorder = useColorModeValue("white", "whiteAlpha.200");
  const fallbackGradient = useColorModeValue(
    "linear(to-br, brand.400, brand.600)",
    "linear(to-br, brand.300, brand.600)"
  );
  const avatarShadow = useColorModeValue(
    "0 8px 24px rgba(15,23,42,0.12)",
    "0 12px 32px rgba(0,0,0,0.45)"
  );

  return (
    <>
      <style>{KEYFRAMES}</style>
      <Box w="100%" py={{ base: 8, md: 10 }} px={4}>
        <Box
          w="100%"
          maxW="lg"
          mx="auto"
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={shellBorder}
          bg={shellBg}
          backdropFilter="blur(12px)"
          boxShadow={shellShadow}
          py={{ base: 8, md: 10 }}
          px={6}
        >
          <VStack spacing={8}>
            <Box position="relative" w="220px" h="220px" mx="auto">
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  position="absolute"
                  left="50%"
                  top="50%"
                  w="85%"
                  h="85%"
                  ml="-42.5%"
                  mt="-42.5%"
                  borderRadius="full"
                  border="1px solid"
                  borderColor={ringColor}
                  animation="no-reco-ping 2.8s ease-out infinite"
                  style={{ animationDelay: `${i * 0.75}s` }}
                  pointerEvents="none"
                />
              ))}

              <Flex
                position="relative"
                zIndex={1}
                w="112px"
                h="112px"
                mx="auto"
                mt="54px"
                borderRadius="full"
                borderWidth="2px"
                borderColor={avatarBorder}
                overflow="hidden"
                align="center"
                justify="center"
                boxShadow={avatarShadow}
              >
                {avatarSrc ? (
                  <Image src={avatarSrc} alt="Your profile photo" w="100%" h="100%" objectFit="cover" />
                ) : (
                  <Flex
                    w="100%"
                    h="100%"
                    align="center"
                    justify="center"
                    bgGradient={fallbackGradient}
                    color="gray.900"
                    fontSize="3xl"
                    fontWeight="bold"
                  >
                    {initials}
                  </Flex>
                )}
              </Flex>
            </Box>

            <VStack spacing={3} textAlign="center" px={1}>
              <Text fontSize="md" fontWeight="semibold" color={titleColor} letterSpacing="-0.02em">
                No matches in range yet
              </Text>
              <Text fontSize="sm" color={muted} lineHeight="tall" maxW="md">
                We&apos;re not seeing new people to show you right now. Check back later—recommendations update as more
                members join or move within range.
              </Text>
            </VStack>

            {onRefresh ? (
              <Button
                type="button"
                size="sm"
                leftIcon={<RefreshCw size={16} />}
                onClick={onRefresh}
                borderRadius="full"
                px={6}
                fontWeight="semibold"
                bgGradient="linear(to-r, brand.300, brand.500)"
                color="gray.900"
                borderWidth="1px"
                borderColor="whiteAlpha.300"
                boxShadow="0 2px 12px rgba(52,211,153,0.22)"
                _hover={{
                  bgGradient: "linear(to-r, brand.400, brand.600)",
                  transform: "translateY(-1px)",
                }}
                _active={{ transform: "translateY(0)" }}
              >
                Refresh scan
              </Button>
            ) : null}
          </VStack>
        </Box>
      </Box>
    </>
  );
}
